import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const init = (ws) => ({
  planned_start: (ws?.schedule_confirmed !== false && ws?.planned_start) || "",
  all_day: !!ws?.all_day,
  start_time: ws?.start_time || "08:00",
  planned_end: ws?.planned_end || "",
  end_time: ws?.end_time || "",
  responsible_id: ws?.responsible_id || "",
  planning_note: ws?.planning_note || "",
});

export function PlanningDialog({ open, onOpenChange, ws, onSaved }) {
  const [f, setF] = useState(init(ws));
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const replan = ws?.schedule_confirmed !== false && !!ws?.planned_start && ws?.category !== "a_planifier";
  useEffect(() => { if (open) { setF(init(ws)); api.get("/users/assignable").then((r) => setUsers(r.data || [])).catch(() => setUsers([])); } }, [open, ws?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const endBeforeStart = f.planned_start && (f.planned_end || f.end_time) &&
    `${f.planned_end || f.planned_start}T${f.end_time || "23:59"}` < `${f.planned_start}T${f.all_day ? "00:00" : f.start_time || "00:00"}`;

  const save = async () => {
    if (!f.planned_start) { toast.error("Indiquez la date de début"); return; }
    if (!f.all_day && !f.start_time) { toast.error("Indiquez l'heure de début ou choisissez « Journée entière »"); return; }
    if (endBeforeStart) { toast.error("La fin prévue ne peut pas être antérieure au début"); return; }
    setSaving(true);
    try {
      await api.post(`/track/worksites/${ws.id}/schedule`, {
        planned_start: f.planned_start, all_day: f.all_day, start_time: f.all_day ? null : f.start_time,
        planned_end: f.planned_end || null, end_time: f.end_time || null,
        responsible_id: f.responsible_id || null, planning_note: f.planning_note,
      });
      toast.success(replan ? "Chantier replanifié" : "Chantier planifié");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="planning-dialog">
        <DialogHeader>
          <DialogTitle>{replan ? "Replanifier le chantier" : "Planifier le chantier"}</DialogTitle>
          <DialogDescription>{ws?.reference} · {ws?.title}{ws?.site_name ? ` — ${ws.site_name}` : ""}. Modification interne : le client n'est pas prévenu automatiquement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Date de début *</Label>
              <Input type="date" value={f.planned_start} onChange={set("planned_start")} data-testid="plan-start-date" /></div>
            <div className="space-y-1.5"><Label>Heure de début</Label>
              <Input type="time" value={f.all_day ? "" : f.start_time} disabled={f.all_day} onChange={set("start_time")} data-testid="plan-start-time" /></div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.all_day} onChange={(e) => setF((s) => ({ ...s, all_day: e.target.checked }))} className="h-4 w-4 accent-emerald-600" data-testid="plan-all-day" />
            Journée entière
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Fin prévue <span className="font-normal text-slate-400">(facultatif)</span></Label>
              <Input type="date" value={f.planned_end} min={f.planned_start || undefined} onChange={set("planned_end")} data-testid="plan-end-date" /></div>
            <div className="space-y-1.5"><Label>Heure de fin <span className="font-normal text-slate-400">(facultatif)</span></Label>
              <Input type="time" value={f.end_time} onChange={set("end_time")} data-testid="plan-end-time" /></div>
          </div>
          {endBeforeStart && <p className="text-xs text-rose-600" data-testid="plan-end-error">La fin prévue ne peut pas être antérieure au début.</p>}
          <div className="space-y-1.5"><Label>Responsable</Label>
            <Select value={f.responsible_id || "none"} onValueChange={(v) => setF((s) => ({ ...s, responsible_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="plan-responsible"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun responsable</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Note de planification interne <span className="font-normal text-slate-400">(facultatif)</span></Label>
            <Textarea rows={2} value={f.planning_note} onChange={set("planning_note")} data-testid="plan-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="plan-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving || !!endBeforeStart} className="bg-emerald-600 hover:bg-emerald-700" data-testid="plan-save">
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <><Icons.CalendarCheck className="mr-1.5 h-4 w-4" />Enregistrer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
